import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  ShoppingCart, DollarSign, Eye, TrendingUp, TrendingDown,
  Package, AlertTriangle, Users, MapPin, Download, Loader2,
  Calendar, Filter, Smartphone, Monitor, Tablet, Clock,
  ArrowUpRight, ArrowDownRight, Truck, BarChart3, Bell, Send, CheckCircle
} from 'lucide-react';

const API_URL = '';

const GOLD = '#D4AF37';
const DARK = '#1A1514';
const COLORS = ['#D4AF37', '#1A1514', '#C87979', '#6B8F71', '#7B68AE', '#E8A87C', '#85677B', '#5B8A9A', '#D4915E', '#8B7355'];

const PERIODS = [
  { id: 'today', label: "Aujourd'hui" },
  { id: 'yesterday', label: 'Hier' },
  { id: 'week', label: '7 jours' },
  { id: 'month', label: '30 jours' },
  { id: 'last_month', label: 'Mois dernier' },
];

function formatDA(amount) {
  return new Intl.NumberFormat('fr-DZ').format(Math.round(amount)) + ' DA';
}

function KpiCard({ icon: Icon, label, value, subValue, trend, color = GOLD }) {
  const isPositive = trend > 0;
  return (
    <div className="bg-white border border-[#E0DFDD] p-4 sm:p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="p-2 bg-[#F5F4F2]">
          <Icon size={18} style={{ color }} />
        </div>
        {trend !== undefined && trend !== 0 && (
          <div className={`flex items-center gap-0.5 text-xs font-medium ${isPositive ? 'text-[#6B8F71]' : 'text-[#C87979]'}`}>
            {isPositive ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <p className="text-2xl font-semibold text-[#1A1514]">{value}</p>
      <p className="text-xs text-[#6B6A68] mt-1">{label}</p>
      {subValue && <p className="text-[10px] text-[#9E9C9A] mt-0.5">{subValue}</p>}
    </div>
  );
}

function ChartCard({ title, children, className = "" }) {
  return (
    <div className={`bg-white border border-[#E0DFDD] p-5 ${className}`}>
      <h3 className="font-medium text-sm mb-4">{title}</h3>
      {children}
    </div>
  );
}

function TableCard({ title, headers, rows, emptyText = "Aucune donnée" }) {
  return (
    <div className="bg-white border border-[#E0DFDD] overflow-hidden">
      <div className="px-5 py-3 border-b border-[#E0DFDD]">
        <h3 className="font-medium text-sm">{title}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-[#FAFAF9]">
              {headers.map((h, i) => (
                <th key={i} className="px-4 py-2.5 text-left text-[#6B6A68] font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={headers.length} className="px-4 py-8 text-center text-[#9E9C9A]">{emptyText}</td></tr>
            ) : rows.map((row, i) => (
              <tr key={i} className="border-t border-[#F5F4F2] hover:bg-[#FAFAF9]">
                {row.map((cell, j) => (
                  <td key={j} className="px-4 py-2.5 whitespace-nowrap">{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const [period, setPeriod] = useState('month');
  const [wilaya, setWilaya] = useState('');
  const [category, setCategory] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // Filter options
  const [filterOptions, setFilterOptions] = useState({ wilayas: [], categories: [] });

  // Data states
  const [kpis, setKpis] = useState(null);
  const [salesChart, setSalesChart] = useState([]);
  const [ordersDist, setOrdersDist] = useState({ by_day: [], by_hour: [] });
  const [topProducts, setTopProducts] = useState([]);
  const [stockAlerts, setStockAlerts] = useState([]);
  const [wilayaData, setWilayaData] = useState([]);
  const [customerData, setCustomerData] = useState(null);
  const [sizeCatData, setSizeCatData] = useState(null);
  const [behaviorData, setBehaviorData] = useState(null);
  const [deliveryData, setDeliveryData] = useState(null);
  const [reportsStatus, setReportsStatus] = useState(null);
  const [triggeringReport, setTriggeringReport] = useState(null);

  // Fetch filter options once
  useEffect(() => {
    fetch(`${API_URL}/api/bloom-gestion-secure/analytics/filters`, { credentials: 'include' })
      .then(res => res.ok ? res.json() : { wilayas: [], categories: [] })
      .then(data => setFilterOptions(data))
      .catch(() => {});
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const params = `period=${period}${wilaya ? `&wilaya=${wilaya}` : ''}${category ? `&category=${category}` : ''}`;
    const opts = { credentials: 'include' };

    try {
      const endpoints = {
        kpis: `analytics/kpis?${params}`,
        sales: `analytics/sales-chart?${params}`,
        dist: `analytics/orders-distribution?${params}`,
        products: `analytics/top-products?${params}`,
        stock: `analytics/stock-alerts`,
        wilayas: `analytics/wilayas?${params}`,
        customers: `analytics/customers?${params}`,
        sizeCat: `analytics/sizes-categories?${params}`,
        behavior: `analytics/behavior?${params}`,
        delivery: `analytics/delivery?${params}`,
      };

      const results = await Promise.allSettled(
        Object.entries(endpoints).map(async ([key, ep]) => {
          const res = await fetch(`${API_URL}/api/bloom-gestion-secure/${ep}`, opts);
          if (!res.ok) throw new Error(`${key} failed`);
          return { key, data: await res.json() };
        })
      );

      for (const r of results) {
        if (r.status === 'fulfilled') {
          const { key, data } = r.value;
          switch (key) {
            case 'kpis': setKpis(data); break;
            case 'sales': setSalesChart(data); break;
            case 'dist': setOrdersDist(data); break;
            case 'products': setTopProducts(data); break;
            case 'stock': setStockAlerts(data); break;
            case 'wilayas': setWilayaData(data); break;
            case 'customers': setCustomerData(data); break;
            case 'sizeCat': setSizeCatData(data); break;
            case 'behavior': setBehaviorData(data); break;
            case 'delivery': setDeliveryData(data); break;
            default: break;
          }
        }
      }
    } catch (err) {
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [period, wilaya, category]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-refresh every 5 min
  useEffect(() => {
    const interval = setInterval(fetchAll, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const handleExport = (type, format = 'csv') => {
    const url = `${API_URL}/api/bloom-gestion-secure/export/${type}?period=${period}&format=${format}${wilaya ? `&wilaya=${wilaya}` : ''}${category ? `&category=${category}` : ''}`;
    window.open(url, '_blank');
  };

  const fetchReportsStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/bloom-gestion-secure/reports/status`, { credentials: 'include' });
      if (res.ok) setReportsStatus(await res.json());
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { fetchReportsStatus(); }, [fetchReportsStatus]);

  const triggerReport = async (type) => {
    setTriggeringReport(type);
    try {
      const res = await fetch(`${API_URL}/api/bloom-gestion-secure/reports/trigger`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ type })
      });
      if (res.ok) {
        const data = await res.json();
        alert(`Rapport ${type} envoyé avec succès !`);
      } else {
        alert('Erreur lors de l\'envoi du rapport');
      }
    } catch (e) {
      alert('Erreur réseau');
    } finally {
      setTriggeringReport(null);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble' },
    { id: 'products', label: 'Produits' },
    { id: 'geography', label: 'Géographie' },
    { id: 'customers', label: 'Clients' },
    { id: 'behavior', label: 'Comportement' },
    { id: 'delivery', label: 'Livraison' },
    { id: 'reports', label: 'Rapports & Alertes' },
  ];

  if (loading && !kpis) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
      </div>
    );
  }

  return (
    <div data-testid="admin-analytics">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-3xl">Analytics</h1>
          <p className="text-sm text-[#6B6A68]">Tableau de bord en temps réel</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center">
          {PERIODS.map(p => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              className={`px-3 py-1.5 text-xs font-medium border transition-colors ${
                period === p.id ? 'bg-[#1A1514] text-white border-[#1A1514]' : 'bg-white text-[#6B6A68] border-[#E0DFDD] hover:border-[#1A1514]'
              }`}
              data-testid={`period-${p.id}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Wilaya + Category Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative">
          <select
            value={wilaya}
            onChange={(e) => setWilaya(e.target.value)}
            className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-[#E0DFDD] bg-white focus:outline-none focus:border-[#D4AF37] cursor-pointer"
            data-testid="filter-wilaya"
          >
            <option value="">Toutes les wilayas</option>
            {filterOptions.wilayas.map(w => (
              <option key={w} value={w}>{w}</option>
            ))}
          </select>
          <MapPin size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9E9C9A] pointer-events-none" />
        </div>

        <div className="relative">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-[#E0DFDD] bg-white focus:outline-none focus:border-[#D4AF37] cursor-pointer"
            data-testid="filter-category"
          >
            <option value="">Toutes les catégories</option>
            {filterOptions.categories.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <Filter size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9E9C9A] pointer-events-none" />
        </div>

        {(wilaya || category) && (
          <button
            onClick={() => { setWilaya(''); setCategory(''); }}
            className="px-3 py-1.5 text-xs text-[#C87979] border border-[#C87979]/30 hover:bg-[#C87979]/5 transition-colors"
            data-testid="clear-filters"
          >
            Réinitialiser les filtres
          </button>
        )}
      </div>

      {/* Export buttons */}
      <div className="bg-white border border-[#E0DFDD] p-4 mb-6" data-testid="export-panel">
        <p className="text-xs uppercase tracking-[0.15em] text-[#6B6A68] font-medium mb-3 flex items-center gap-2">
          <Download size={12} />
          Exports — période et filtres actifs
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
          {[
            { type: 'orders', label: 'Commandes' },
            { type: 'customers', label: 'Clients' },
            { type: 'top-products', label: 'Top Produits' },
            { type: 'wilayas', label: 'Par Wilaya' },
            { type: 'stock', label: 'Stock' },
          ].map(exp => (
            <div key={exp.type} className="border border-[#E0DFDD] hover:border-[#D4AF37] transition-colors flex items-center justify-between p-2.5">
              <span className="text-xs font-medium">{exp.label}</span>
              <div className="flex gap-1">
                <button
                  onClick={() => handleExport(exp.type, 'csv')}
                  className="px-2 py-1 text-[10px] font-semibold bg-[#F5F4F2] hover:bg-[#1A1514] hover:text-white transition-colors"
                  data-testid={`export-${exp.type}-csv`}
                  title="Télécharger en CSV"
                >
                  CSV
                </button>
                <button
                  onClick={() => handleExport(exp.type, 'xlsx')}
                  className="px-2 py-1 text-[10px] font-semibold bg-[#1A1514] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#1A1514] transition-colors"
                  data-testid={`export-${exp.type}-xlsx`}
                  title="Télécharger en Excel"
                >
                  XLSX
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-[#E0DFDD] overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-xs font-medium whitespace-nowrap transition-colors border-b-2 -mb-[1px] ${
              activeTab === tab.id ? 'border-[#D4AF37] text-[#1A1514]' : 'border-transparent text-[#6B6A68] hover:text-[#1A1514]'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* KPIs — Always visible */}
      {kpis && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6" data-testid="kpi-grid">
          <KpiCard icon={ShoppingCart} label="Commandes" value={kpis.orders} trend={kpis.growth_orders} subValue={`vs ${kpis.prev_orders} précédemment`} />
          <KpiCard icon={DollarSign} label="Chiffre d'affaires" value={formatDA(kpis.revenue)} trend={kpis.growth_revenue} color="#6B8F71" subValue={`vs ${formatDA(kpis.prev_revenue)}`} />
          <KpiCard icon={Eye} label="Visiteurs" value={kpis.visitors} color="#7B68AE" />
          <KpiCard icon={TrendingUp} label="Taux de conversion" value={`${kpis.conversion_rate}%`} color={GOLD} />
          <KpiCard icon={Package} label="En attente" value={kpis.pending_orders} color="#E8A87C" />
          <KpiCard icon={AlertTriangle} label="Paniers abandonnés" value={kpis.abandoned_carts} color="#C87979" />
          <KpiCard icon={DollarSign} label="Benefice net" value={formatDA(kpis.profit || 0)} color="#2E7D32" subValue={`Marge: ${kpis.margin_pct || 0}%`} />
          <KpiCard icon={TrendingUp} label="Cout total" value={formatDA(kpis.total_cost || 0)} color="#8B5E3C" />
        </div>
      )}

      {/* TAB: Overview */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Sales line chart */}
          <ChartCard title="Évolution des ventes (DA)">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={salesChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E0DFDD" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => formatDA(v)} labelFormatter={l => `Date: ${l}`} />
                <Line type="monotone" dataKey="revenue" stroke={GOLD} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Orders by day */}
            <ChartCard title="Commandes par jour de la semaine">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={ordersDist.by_day}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0DFDD" />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="orders" fill={GOLD} radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Orders by hour */}
            <ChartCard title="Commandes par heure">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={ordersDist.by_hour}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0DFDD" />
                  <XAxis dataKey="hour" tick={{ fontSize: 9 }} interval={2} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="orders" fill={DARK} radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Stock alerts */}
          {stockAlerts.length > 0 && (
            <TableCard
              title={`Alertes stock (${stockAlerts.length} produits)`}
              headers={['Produit', 'Stock total', 'Tailles en rupture']}
              rows={stockAlerts.map(a => [
                a.name,
                <span className={a.total_stock <= 3 ? 'text-[#C87979] font-bold' : ''}>{a.total_stock}</span>,
                a.zero_sizes.length > 0 ? <span className="text-[#C87979]">{a.zero_sizes.join(', ')}</span> : '-'
              ])}
            />
          )}
        </div>
      )}

      {/* TAB: Products */}
      {activeTab === 'products' && (
        <div className="space-y-6">
          <TableCard
            title="Top 10 produits"
            headers={['#', 'Produit', 'Ventes', 'CA', 'Benefice', 'Marge', 'Vues', 'Conv.']}
            rows={topProducts.map((p, i) => [
              i + 1,
              <span className="font-medium">{p.name}</span>,
              p.sales,
              formatDA(p.revenue),
              formatDA(p.profit || 0),
              p.margin_pct ? `${p.margin_pct}%` : '-',
              p.views || '-',
              p.conversion ? `${p.conversion}%` : '-'
            ])}
          />

          {/* Size distribution */}
          {sizeCatData && (
            <div className="grid md:grid-cols-2 gap-6">
              <ChartCard title="Distribution des tailles">
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={sizeCatData.size_distribution}
                      dataKey="count"
                      nameKey="size"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ size, pct }) => `${size} (${pct}%)`}
                    >
                      {sizeCatData.size_distribution.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Performance par catégorie (DA)">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={sizeCatData.category_performance} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E0DFDD" />
                    <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                    <YAxis dataKey="category" type="category" tick={{ fontSize: 10 }} width={100} />
                    <Tooltip formatter={(v) => formatDA(v)} />
                    <Bar dataKey="revenue" fill={GOLD} radius={[0, 2, 2, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          )}
        </div>
      )}

      {/* TAB: Geography */}
      {activeTab === 'geography' && (
        <div className="space-y-6">
          <TableCard
            title="Classement par wilaya"
            headers={['Wilaya', 'Commandes', 'CA', 'Panier moyen', 'Domicile %', 'Bureau %', 'Produit phare']}
            rows={wilayaData.map(w => [
              <span className="font-medium">{w.wilaya}</span>,
              w.orders,
              formatDA(w.revenue),
              formatDA(w.avg_order),
              `${w.home_pct}%`,
              `${w.bureau_pct}%`,
              <span className="text-[10px]">{w.top_product}</span>
            ])}
          />
        </div>
      )}

      {/* TAB: Customers */}
      {activeTab === 'customers' && customerData && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KpiCard icon={Users} label="Total clients" value={customerData.total_customers} color={DARK} />
            <KpiCard icon={Users} label="Nouveaux (période)" value={customerData.new_this_period} color="#6B8F71" subValue={`vs ${customerData.new_prev_period} avant`} />
            <KpiCard icon={TrendingUp} label="Taux de fidélité" value={`${customerData.repeat_rate}%`} color={GOLD} />
            <KpiCard icon={DollarSign} label="Panier moyen" value={formatDA(customerData.avg_order_value)} color="#7B68AE" />
          </div>

          {/* Segments */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Nouveaux (1 commande)', count: customerData.segments.new, color: '#5B8A9A' },
              { label: 'Réguliers (2-4)', count: customerData.segments.regular, color: GOLD },
              { label: 'VIP (5+)', count: customerData.segments.vip, color: '#6B8F71' },
            ].map(seg => (
              <div key={seg.label} className="bg-white border border-[#E0DFDD] p-4 text-center">
                <p className="text-2xl font-semibold" style={{ color: seg.color }}>{seg.count}</p>
                <p className="text-xs text-[#6B6A68] mt-1">{seg.label}</p>
              </div>
            ))}
          </div>

          {/* Top VIP */}
          <TableCard
            title="Top 20 clients VIP"
            headers={['Client', 'Téléphone', 'Commandes', 'CA Total', 'Dernière commande']}
            rows={customerData.top_vip.map(c => [
              <span className="font-medium">{c.name}</span>,
              c.phone,
              c.orders,
              formatDA(c.revenue),
              c.last_order ? c.last_order.slice(0, 10) : '-'
            ])}
          />
        </div>
      )}

      {/* TAB: Behavior */}
      {activeTab === 'behavior' && behaviorData && (
        <div className="space-y-6">
          <div className="grid sm:grid-cols-3 gap-3">
            {Object.entries(behaviorData.devices).map(([device, data]) => (
              <div key={device} className="bg-white border border-[#E0DFDD] p-4 flex items-center gap-3">
                {device === 'mobile' && <Smartphone size={20} className="text-[#D4AF37]" />}
                {device === 'desktop' && <Monitor size={20} className="text-[#1A1514]" />}
                {device === 'tablet' && <Tablet size={20} className="text-[#7B68AE]" />}
                <div>
                  <p className="text-lg font-semibold">{data.pct}%</p>
                  <p className="text-xs text-[#6B6A68] capitalize">{device}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Sources */}
            <ChartCard title="Sources de trafic">
              {Object.keys(behaviorData.sources).length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={Object.entries(behaviorData.sources).map(([name, count]) => ({ name, count }))}
                      dataKey="count"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={({ name, count }) => `${name}: ${count}`}
                    >
                      {Object.keys(behaviorData.sources).map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-[#9E9C9A] text-center py-8">Aucune donnée de source</p>
              )}
            </ChartCard>

            {/* Top pages */}
            <TableCard
              title="Pages les plus visitées"
              headers={['Page', 'Vues']}
              rows={behaviorData.top_pages.map(p => [p.url, p.views])}
              emptyText="Aucune donnée de navigation"
            />
          </div>
        </div>
      )}

      {/* TAB: Delivery */}
      {activeTab === 'delivery' && deliveryData && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KpiCard icon={Package} label="Total commandes" value={deliveryData.total_orders} color={DARK} />
            <KpiCard icon={Truck} label="Domicile" value={`${deliveryData.home_pct}%`} color={GOLD} subValue={`${deliveryData.home_delivery} commandes`} />
            <KpiCard icon={MapPin} label="Bureau" value={`${deliveryData.bureau_pct}%`} color="#5B8A9A" subValue={`${deliveryData.bureau_pickup} commandes`} />
            <KpiCard icon={AlertTriangle} label="Annulées" value={deliveryData.cancelled} color="#C87979" />
          </div>

          {/* Status breakdown */}
          <ChartCard title="Répartition par statut">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={deliveryData.by_status}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E0DFDD" />
                <XAxis dataKey="status" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="count" fill={GOLD} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}

      {/* TAB: Reports & Alerts */}
      {activeTab === 'reports' && (
        <div className="space-y-6" data-testid="reports-tab">
          {/* Scheduler Status */}
          <div className="bg-white border border-[#E0DFDD] p-6">
            <div className="flex items-center gap-2 mb-4">
              <Bell size={18} className="text-[#D4AF37]" />
              <h3 className="font-medium text-sm">Planificateur de rapports</h3>
              {reportsStatus?.scheduler_running && (
                <span className="ml-auto flex items-center gap-1 text-xs text-[#6B8F71]">
                  <CheckCircle size={12} /> Actif
                </span>
              )}
            </div>

            {reportsStatus?.jobs?.length > 0 ? (
              <div className="space-y-2">
                {reportsStatus.jobs.map(job => (
                  <div key={job.id} className="flex items-center justify-between py-2 border-b border-[#F5F4F2] last:border-0">
                    <div>
                      <p className="text-sm font-medium capitalize">{job.id.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-[#6B6A68]">
                        Prochaine exécution : {job.next_run ? new Date(job.next_run).toLocaleString('fr-FR') : 'N/A'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#9E9C9A]">Aucun job programmé</p>
            )}
          </div>

          {/* Manual Trigger */}
          <div className="bg-white border border-[#E0DFDD] p-6">
            <div className="flex items-center gap-2 mb-4">
              <Send size={18} className="text-[#1A1514]" />
              <h3 className="font-medium text-sm">Envoyer un rapport maintenant</h3>
            </div>
            <p className="text-xs text-[#6B6A68] mb-4">
              Déclenchez manuellement l'envoi d'un rapport par email à l'adresse admin.
            </p>

            <div className="grid sm:grid-cols-3 gap-3">
              {[
                { type: 'daily', label: 'Rapport journalier', desc: "Résumé d'aujourd'hui : commandes, CA, produit star, wilaya active" },
                { type: 'weekly', label: 'Rapport hebdomadaire', desc: 'Bilan des 7 derniers jours : top produits, wilayas, clients' },
                { type: 'monthly', label: 'Rapport mensuel', desc: 'Bilan du mois : CA vs mois précédent, top 5 produits et wilayas' },
              ].map(report => (
                <div key={report.type} className="border border-[#E0DFDD] p-4">
                  <h4 className="font-medium text-sm mb-1">{report.label}</h4>
                  <p className="text-[10px] text-[#6B6A68] mb-3">{report.desc}</p>
                  <button
                    onClick={() => triggerReport(report.type)}
                    disabled={triggeringReport === report.type}
                    className="w-full py-2 text-xs bg-[#1A1514] text-white hover:bg-[#D4AF37] transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
                    data-testid={`trigger-${report.type}`}
                  >
                    {triggeringReport === report.type ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Send size={12} />
                    )}
                    Envoyer
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Alerts Info */}
          <div className="bg-white border border-[#E0DFDD] p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle size={18} className="text-[#E8A87C]" />
              <h3 className="font-medium text-sm">Alertes automatiques actives</h3>
            </div>
            <div className="space-y-3">
              {[
                { icon: ShoppingCart, text: 'Nouvelle commande — notification immédiate', active: true },
                { icon: AlertTriangle, text: 'Stock critique (moins de 3 unités) — vérification toutes les 6h', active: true },
                { icon: Users, text: 'Nouveau client VIP (5ème commande) — notification immédiate', active: true },
                { icon: Package, text: '0 commandes en 24h — alerte quotidienne à 21h', active: true },
              ].map((alert, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b border-[#F5F4F2] last:border-0">
                  <alert.icon size={16} className="text-[#D4AF37] flex-shrink-0" />
                  <span className="text-xs text-[#1A1514] flex-1">{alert.text}</span>
                  <span className={`text-[10px] px-2 py-0.5 font-medium ${alert.active ? 'bg-[#f0fdf4] text-[#166534]' : 'bg-[#fef2f2] text-[#991b1b]'}`}>
                    {alert.active ? 'Actif' : 'Inactif'}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-[#9E9C9A] mt-4">
              Les alertes sont envoyées à l'adresse admin configurée. Lorsque Brevo sera activé, les emails seront envoyés en temps réel. En attendant, les alertes sont visibles dans les logs du backend.
            </p>
          </div>
        </div>
      )}

      {/* Loading overlay */}
      {loading && (
        <div className="fixed bottom-4 right-4 bg-[#1A1514] text-white text-xs px-4 py-2 flex items-center gap-2 shadow-lg z-50">
          <Loader2 size={14} className="animate-spin" />
          Mise à jour...
        </div>
      )}
    </div>
  );
}
